import React, { FC } from 'react';
import styled from 'styled-components';

import BankratePng from '../../../assets/bankrate.png';
import BestMoneySvg from '../../../assets/best-money.svg';
import CreditCardsDotComPng from '../../../assets/credit-cards-dot-com.png';
import CreditSesamePng from '../../../assets/credit-sesame.png';
import ExperianPng from '../../../assets/experian.png';
import FionaSvg from '../../../assets/fiona.svg';
import CreditKarmaSvg from '../../../assets/intuit-credit-karma.svg';
import ITMediaPng from '../../../assets/itmedia-tfo.png';
import LendingTreeSvg from '../../../assets/lending-tree.svg';
import MoneyLionSvg from '../../../assets/money-lion.svg';
import NavPng from '../../../assets/nav.png';
import WalletHubPng from '../../../assets/wallet-hub.png';
import { CARD_BREAKPOINT, colors, MOBILE_BREAKPOINT } from '../../constants';
import { prefixBaseRoute, prefixRoute } from '../../utilities/routes';
import ProsperLogo from '../ProsperLogo';

// Nav Logos

// Displays Prosper Logo in Nav
// Conditionally displays partner bank logo ONLY IN /apply PAGE if ref param is provided

// Interface --------------------------------------------------------------------------------------
interface NavLogosProps {
  partnerCampaign?: string;
}

// Styles -----------------------------------------------------------------------------------------
const Container = styled.div`
  display: flex;
  align-items: center;
`;
Container.displayName = 'Container';

// Centers logo within
const Anchor = styled.a``;
Anchor.displayName = 'Anchor';

// For the separator if partner ref exists
const Separator = styled.div`
  margin: 0 1.5rem;
  padding: 0 0 0.1875rem;
  font-size: 1rem;
  color: ${colors.BORDER_SECONDARY};
  @media (max-width: ${CARD_BREAKPOINT}) {
    margin: 0 0.6875rem;
  }
`;
Separator.displayName = 'Separator';

const ProsperNavLogo = styled(ProsperLogo)`
  @media (max-width: ${CARD_BREAKPOINT}) {
    height: 1.1875rem;
  }
`;
ProsperNavLogo.displayName = 'ProsperNavLogo';

const CreditKarmaLogo = styled(CreditKarmaSvg)`
  height: 2rem;
  width: auto;
  @media (max-width: ${CARD_BREAKPOINT}) {
    height: 1.625rem;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    height: 1.5rem;
  }
`;
CreditKarmaLogo.displayName = 'CreditKarmaLogo';

const CreditCardsDotComLogo = styled.img`
  height: 1.4375rem;
  width: auto;
  @media (max-width: ${CARD_BREAKPOINT}) {
    height: 0.9375rem;
    width: auto;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    height: 0.8125rem;
    width: auto;
  }
`;
CreditCardsDotComLogo.displayName = 'CreditCardsDotComLogo';

const BankrateLogo = styled.img`
  height: 1.375rem;
  width: auto;
  margin-top: -0.125rem;
  @media (max-width: ${CARD_BREAKPOINT}) {
    height: 1.25rem;
    width: auto;
  }
`;
BankrateLogo.displayName = 'BankrateLogo';

const WalletHubLogo = styled.img`
  height: 2.125rem;
  width: auto;
  @media (max-width: ${CARD_BREAKPOINT}) {
    height: 1.5rem;
    width: auto;
  }
`;
WalletHubLogo.displayName = 'WalletHubLogo';

const ExperianLogo = styled.img`
  height: 2.75rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    height: 2.25rem;
  }
`;
ExperianLogo.displayName = 'ExperianLogo';

const ITMediaLogo = styled.img`
  height: 1.5rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    height: 1.25rem;
  }
`;
ITMediaLogo.displayName = 'ITMediaLogo';

const NavLogo = styled.img`
  height: 1.3125rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    height: 1.1875rem;
  }
`;
NavLogo.displayName = 'NavLogo';

const BestMoneyLogo = styled(BestMoneySvg)`
  margin: 0.25rem 0 0;
  height: 100%;
  width: auto;
`;
BestMoneyLogo.displayName = 'BestMoneyLogo';

const MoneyLionLogo = styled(MoneyLionSvg)`
  height: 2.125rem;
  width: auto;
  @media (max-width: ${CARD_BREAKPOINT}) {
    height: 1.5rem;
    width: auto;
  }
`;
MoneyLionLogo.displayName = 'MoneyLionLogo';

const FionaLogo = styled(FionaSvg)`
  height: 1.75rem;
  width: auto;
  @media (max-width: ${CARD_BREAKPOINT}) {
    height: 1.5rem;
    width: auto;
  }
`;
FionaLogo.displayName = 'FionaLogo';

const LendingTreeLogo = styled(LendingTreeSvg)`
  height: 2.75rem;
  margin-top: -0.5rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    height: 2.25rem;
    margin-top: -0.25rem;
  }
`;
LendingTreeLogo.displayName = 'LendingTreeLogo';

const CreditSesameLogo = styled.img`
  height: 1.625rem;
  width: auto;
  margin-bottom: 0.5625rem;
  @media (max-width: ${CARD_BREAKPOINT}) {
    height: 1.5rem;
    width: auto;
    margin-bottom: 0.5rem;
  }
`;
CreditSesameLogo.displayName = 'CreditSesameLogo';

// Component --------------------------------------------------------------------------------------
export const NavLogos: FC<NavLogosProps> = ({ partnerCampaign = '' }) => {
  // Account for trailing slashes in path (ie. /credit-card/apply/?crc= vs. /credit-card/apply?crc=)
  const isMainApplyPage =
    window.location.pathname.replace(/\/+$/, '') === prefixRoute('');

  return (
    <Container>
      <Anchor
        href={prefixBaseRoute('')}
        aria-label="Prosper Credit Card Home Page"
      >
        <ProsperNavLogo id="prosper-nav-logo" data-testid="prosper-nav-logo" />
      </Anchor>
      {partnerCampaign && isMainApplyPage && (
        <>
          {partnerCampaign !== 'EQUIFAX' && <Separator>|</Separator>}
          {partnerCampaign === 'CK' && (
            <Anchor
              href="https://www.creditkarma.com/"
              aria-label="Credit Karma Home Page"
              target="_blank"
            >
              <CreditKarmaLogo
                id="credit-karma-nav-logo"
                data-testid="credit-karma-nav-logo"
              />
            </Anchor>
          )}
          {partnerCampaign === 'RV-CCDC' && (
            <Anchor
              href="https://www.creditcards.com/"
              aria-label="Credit Cards Dot Com Home Page"
              target="_blank"
            >
              <CreditCardsDotComLogo
                src={CreditCardsDotComPng}
                alt="credit-cards-dot-com-logo"
                id="credit-cards-dot-com-logo"
                data-testid="credit-cards-dot-com-logo"
              />
            </Anchor>
          )}
          {partnerCampaign === 'RV-BR' && (
            <Anchor
              href="https://www.bankrate.com/"
              aria-label="Bankrate Home Page"
              target="_blank"
            >
              <BankrateLogo
                src={BankratePng}
                alt="bankrate-logo"
                id="bankrate-logo"
                data-testid="bankrate-logo"
              />
            </Anchor>
          )}
          {partnerCampaign === 'WH' && (
            <Anchor
              href="https://wallethub.com/"
              aria-label="WalletHub Home Page"
              target="_blank"
            >
              <WalletHubLogo
                src={WalletHubPng}
                alt="wallet-hub-logo"
                id="wallet-hub-logo"
                data-testid="wallet-hub-logo"
              />
            </Anchor>
          )}
          {partnerCampaign === 'EXPERIAN' && (
            <Anchor
              href="https://www.experian.com/"
              aria-label="Experian Home Page"
              target="_blank"
            >
              <ExperianLogo
                src={ExperianPng}
                alt="experian-logo"
                id="experian-logo"
                data-testid="experian-logo"
              />
            </Anchor>
          )}
          {partnerCampaign === 'ITMEDIA' && (
            <Anchor
              href="https://topfiveoffers.com/"
              aria-label="Top Five Offers Home Page"
              target="_blank"
            >
              <ITMediaLogo
                src={ITMediaPng}
                alt="itmedia-tfo-logo"
                id="itmedia-tfo-logo"
                data-testid="itmedia-tfo-logo"
              />
            </Anchor>
          )}
          {partnerCampaign === 'BESTMONEY' && (
            <Anchor
              href="https://www.bestmoney.com/"
              aria-label="BestMoney Home Page"
              target="_blank"
            >
              <BestMoneyLogo
                id="best-money-logo"
                data-testid="best-money-logo"
              />
            </Anchor>
          )}
          {partnerCampaign === 'MONEYLION' && (
            <Anchor
              href="https://www.moneylion.com/"
              aria-label="MoneyLion Home Page"
              target="_blank"
            >
              <MoneyLionLogo
                id="money-lion-logo"
                data-testid="money-lion-logo"
              />
            </Anchor>
          )}
          {partnerCampaign === 'FIONA' && (
            <Anchor
              href="https://www.fiona.com/"
              aria-label="Fiona Home Page"
              target="_blank"
            >
              <FionaLogo id="fiona-logo" data-testid="fiona-logo" />
            </Anchor>
          )}
          {partnerCampaign === 'LT' && (
            <Anchor
              href="https://www.lendingtree.com/"
              aria-label="LendingTree Home Page"
              target="_blank"
            >
              <LendingTreeLogo
                id="lending-tree-logo"
                data-testid="lending-tree-logo"
              />
            </Anchor>
          )}
          {partnerCampaign === 'CREDITSESAME' && (
            <Anchor
              href="https://www.creditsesame.com/"
              aria-label="Credit Sesame Home Page"
              target="_blank"
            >
              <CreditSesameLogo
                src={CreditSesamePng}
                alt="credit-sesame-logo"
                id="credit-sesame-logo"
                data-testid="credit-sesame-logo"
              />
            </Anchor>
          )}
          {partnerCampaign === 'NAV' && (
            <Anchor
              href="https://www.nav.com/"
              aria-label="Nav Home Page"
              target="_blank"
            >
              <NavLogo
                src={NavPng}
                alt="nav-logo"
                id="nav-logo"
                data-testid="nav-logo"
              />
            </Anchor>
          )}
        </>
      )}
    </Container>
  );
};
